import { useEffect, useRef } from "react";
import { Campo } from "@/componentes/Campo";
import { Dica } from "@/componentes/Dica";
import { Esqueleto } from "@/componentes/Esqueleto";
import { Interruptor } from "@/componentes/Interruptor";
import { PainelRecolhivel } from "@/componentes/PainelRecolhivel";
import { Selecao } from "@/componentes/Selecao";
import type { Convenio } from "@/api/contrato";
import {
  MODOS_DE_REGISTRO,
  camposDoBanco,
  opcoesDeCNAB,
  opcoesDeCarteira,
  usaWebService,
  type CampoDoConvenio,
  type ErrosDoConvenio,
  type ModoDeRegistro,
  type RegrasDoConvenio,
  type ValoresDoConvenio,
} from "./convenio";

/*
 * Os campos do convênio, nos dois painéis da GUI da TecnoSpeed, iguais na criação e na
 * edição (design D4).
 *
 * `required` não entra aqui: um campo obrigatório dentro de `<details>` fechado reprova
 * o envio sem o navegador conseguir apontá-lo, e o botão parece morto. Quem exige é a
 * validação do formulário, e o efeito abaixo abre o painel do campo recusado.
 *
 * O próximo nosso número também não entra: ele é da criação, e mora em `NovoConvenio`.
 */
const DICA_DA_JANELA =
  "Já há boleto emitido com este dado. A correção é feita no banco e com a TecnoSpeed.";

const DICA_DO_ESPELHO = "Alterar aqui não muda o cadastro no provider.";

export function FormularioDeConvenio({
  valores,
  erros,
  regras,
  gravado,
  janelaFechada = false,
  aoMudar,
}: {
  valores: ValoresDoConvenio;
  erros: ErrosDoConvenio;
  regras: RegrasDoConvenio;
  /** O convênio da edição. Na criação, `undefined`. */
  gravado?: Convenio;
  /** Trava o que viaja no boleto (design D8). */
  janelaFechada?: boolean;
  aoMudar: <C extends CampoDoConvenio>(campo: C, valor: ValoresDoConvenio[C]) => void;
}) {
  const raiz = useRef<HTMLDivElement>(null);

  useEffect(() => {
    for (const campo of raiz.current?.querySelectorAll('[aria-invalid="true"]') ?? []) {
      const painel = campo.closest("details");
      if (painel !== null) painel.open = true;
    }
  }, [erros]);

  const edicao = gravado !== undefined;
  const opcionais = camposDoBanco(regras, gravado);
  const webService = usaWebService(valores.modo_registro);

  /** O campo que é espelho do provider avisa, na edição, que corrigir aqui não muda lá. */
  function dicaDeEspelho(dica?: string): string | undefined {
    if (!edicao) return dica;
    return dica === undefined ? DICA_DO_ESPELHO : `${dica} ${DICA_DO_ESPELHO}`;
  }

  const dicaDoNumero = [regras.orientacao, "Precisa ser igual ao cadastro no provider, que é à parte."]
    .filter((texto) => texto !== undefined)
    .join(" ");

  const cnabFixo =
    webService &&
    regras.cnabComWebService !== "" &&
    valores.padrao_cnab === regras.cnabComWebService;

  // Valor antigo que o catálogo não aceita fica editável: travado, ele teria erro sem saída.
  const tipoFixo =
    regras.tipoWebServiceFixo &&
    (valores.tipo_webservice === "" ||
      valores.tipo_webservice.toLowerCase() === regras.tipoWebService.toLowerCase());

  return (
    <div ref={raiz} className="flex flex-col gap-3">
      <PainelRecolhivel titulo="Dados">
        <Campo
          rotulo="Número do convênio"
          dica={janelaFechada ? DICA_DA_JANELA : dicaDoNumero}
          value={valores.numero}
          onChange={(evento) => aoMudar("numero", evento.target.value)}
          erro={erros.numero}
          disabled={janelaFechada}
          aria-required
          autoFocus={!edicao}
          autoComplete="off"
        />

        <Campo
          rotulo="Descrição"
          value={valores.descricao}
          onChange={(evento) => aoMudar("descricao", evento.target.value)}
          erro={erros.descricao}
          aria-required={!edicao}
          autoComplete="off"
        />

        <div className="grid grid-cols-3 gap-2">
          {regras.carteiras.length > 0 ? (
            <Selecao
              rotulo="Carteira"
              dica={janelaFechada ? DICA_DA_JANELA : undefined}
              opcoes={opcoesDeCarteira(regras, valores.carteira)}
              value={valores.carteira}
              onChange={(evento) => aoMudar("carteira", evento.target.value)}
              erro={erros.carteira}
              disabled={janelaFechada}
              aria-required
            />
          ) : (
            <Campo
              rotulo="Carteira"
              dica={janelaFechada ? DICA_DA_JANELA : undefined}
              value={valores.carteira}
              onChange={(evento) => aoMudar("carteira", evento.target.value)}
              erro={erros.carteira}
              disabled={janelaFechada}
              aria-required
              autoComplete="off"
            />
          )}

          <Campo
            rotulo="Variação"
            dica={janelaFechada ? DICA_DA_JANELA : undefined}
            value={valores.variacao}
            onChange={(evento) => aoMudar("variacao", evento.target.value)}
            erro={erros.variacao}
            disabled={janelaFechada}
            autoComplete="off"
          />

          <Selecao
            rotulo="Padrão CNAB"
            dica={dicaDeEspelho(
              cnabFixo ? `Com WebService, o padrão é ${regras.cnabComWebService}.` : undefined,
            )}
            opcoes={opcoesDeCNAB(valores.padrao_cnab)}
            value={valores.padrao_cnab}
            onChange={(evento) => aoMudar("padrao_cnab", evento.target.value)}
            erro={erros.padrao_cnab}
            disabled={cnabFixo}
            aria-required={regras.exigeCNAB}
          />
        </div>
      </PainelRecolhivel>

      <PainelRecolhivel titulo="Configurações">
        <Selecao
          rotulo="Modo de registro"
          dica={dicaDeEspelho()}
          opcoes={MODOS_DE_REGISTRO}
          value={valores.modo_registro}
          onChange={(evento) => aoMudar("modo_registro", evento.target.value as ModoDeRegistro)}
          erro={erros.modo_registro}
        />

        <LinhaDeInterruptor
          rotulo="Registro instantâneo"
          dica="Segue o modo de registro: ligado em WebService e em Misto."
          ligado={webService}
          desabilitado
        />

        {webService && (
          <Campo
            rotulo="Tipo de WebService"
            dica={dicaDeEspelho()}
            value={valores.tipo_webservice || (tipoFixo ? regras.tipoWebService : "")}
            onChange={(evento) => aoMudar("tipo_webservice", evento.target.value)}
            erro={erros.tipo_webservice}
            disabled={tipoFixo}
            autoComplete="off"
          />
        )}

        {opcionais.contrato && (
          <Campo
            rotulo="Número do contrato"
            dica={dicaDeEspelho(
              regras.exigeContrato
                ? "Obrigatório com WebService, opcional em CNAB. Confirme com a agência."
                : undefined,
            )}
            value={valores.numero_contrato}
            onChange={(evento) => aoMudar("numero_contrato", evento.target.value)}
            erro={erros.numero_contrato}
            aria-required={regras.exigeContrato && webService}
            autoComplete="off"
          />
        )}

        {opcionais.remessa && (
          <Campo
            rotulo="Número da remessa"
            dica={dicaDeEspelho(regras.exigeRemessa ? "O número atual da remessa." : undefined)}
            value={valores.numero_remessa}
            onChange={(evento) => aoMudar("numero_remessa", evento.target.value)}
            erro={erros.numero_remessa}
            aria-required={regras.exigeRemessa}
            autoComplete="off"
          />
        )}

        <LinhaDeInterruptor
          rotulo="Reiniciar remessa diariamente"
          ligado={valores.reiniciar_remessa_diariamente}
          aoAlternar={() =>
            aoMudar("reiniciar_remessa_diariamente", !valores.reiniciar_remessa_diariamente)
          }
        />

        {opcionais.byte && (
          <Campo
            rotulo="Byte do nosso número"
            dica={
              janelaFechada
                ? DICA_DA_JANELA
                : "Um dígito, informado pelo banco. Vale para a emissão e para conciliar o retorno. Vazio: o provider assume 2."
            }
            value={valores.byte_nosso_numero}
            onChange={(evento) => aoMudar("byte_nosso_numero", evento.target.value)}
            erro={erros.byte_nosso_numero}
            disabled={janelaFechada}
            inputMode="numeric"
            maxLength={1}
            autoComplete="off"
            className="w-16"
          />
        )}
      </PainelRecolhivel>
    </div>
  );
}

function LinhaDeInterruptor({
  rotulo,
  dica,
  ligado,
  aoAlternar,
  desabilitado = false,
}: {
  rotulo: string;
  dica?: string;
  ligado: boolean;
  aoAlternar?: () => void;
  desabilitado?: boolean;
}) {
  return (
    <div className="flex h-9 items-center justify-between gap-2">
      <div className="flex items-center gap-1">
        <span className="rotulo">{rotulo}</span>
        {dica !== undefined && <Dica texto={dica} />}
      </div>
      <Interruptor
        ligado={ligado}
        rotulo={rotulo}
        aoAlternar={aoAlternar ?? (() => undefined)}
        desabilitado={desabilitado}
      />
    </div>
  );
}

/** Enquanto o catálogo não chega: com a altura do formulário, sem salto no modal. */
export function EsqueletoDoFormulario() {
  return (
    <div aria-busy="true">
      <Esqueleto className="h-[440px]" />
    </div>
  );
}
